import { Check, CircleSlash, Keyboard, LogOut, Moon, Sun, SunMoon } from 'lucide-react';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { sourcesFor } from '../lib/sources';
import { fleetQuery, useFacets } from '../search/hooks';
import { ApprovalKeys, Enrollment } from '../settings/Enrollment';
import { SecondFactors } from '../settings/SecondFactors';
import { MyInvitations, People } from '../settings/People';
import { SupportContactSetting } from '../settings/SupportContact';
import { SettingsList, SettingsListPreferences } from '../settings/SettingsList';
import { AgentAccess } from '../settings/AgentAccess';
import { absolute, timeZone } from '../lib/time';
import { type Theme, useTheme } from '../lib/theme';
import { useScope, useWorkspaces } from '../scope/scope';
import { useSession } from '../session/session';
import { useShell } from '../shell/AppShell';
import { Button } from '../ui/Button';
import { cn } from '../ui/cn';
import { Card } from '../ui/misc';
import { PageHeader } from './PageHeader';

function Section({ id, title, description, children }: { id?: string; title: string; description?: string; children: React.ReactNode }) {
  return (
    <section id={id} className="grid scroll-mt-20 gap-4 border-t border-line pt-6 md:grid-cols-[240px_minmax(0,1fr)]">
      <div><h2 className="font-semibold">{title}</h2>{description && <p className="mt-1 text-[13px] text-ink-3">{description}</p>}</div>
      <div className="min-w-0">{children}</div>
    </section>
  );
}

export function SettingsPage() {
  const { session, signOut, signingOut } = useSession();
  const { mspId, role, memberships } = useScope();
  const fleet = useFacets(fleetQuery);
  const capabilityLabel = { 'devices.enroll': 'Add devices', 'diagnostics.run': 'Run runbooks', 'scripts.run': 'Run PowerShell', 'jobs.approve': 'Approve jobs', 'remote.connect': 'Remote in', 'end_users.manage': 'Manage end users' } as const;
  const myCaps = [...new Set(memberships.flatMap(m => m.capabilities))];
  const workspaces = useWorkspaces();
  const { showShortcuts } = useShell();
  const [theme, setTheme] = useTheme();
  const { hash } = useLocation();
  useEffect(() => { if (hash) document.getElementById(hash.slice(1))?.scrollIntoView(); }, [hash]);
  const themes: Array<[Theme, string, typeof Sun]> = [['system', 'Match system', SunMoon], ['light', 'Light', Sun], ['dark', 'Dark', Moon]];

  return (
    <SettingsListPreferences personId={session.personId} mspId={mspId}>
    <div className="max-w-4xl space-y-6">
      <PageHeader title="Settings" description="Your account, workspaces and what this console can see." />
      <Section title="Account" description={`${session.dataset === 'synthetic' ? 'Signed in through the local test sign-in; Google isn’t contacted.' : 'Signed in with Google and a second factor.'} Access comes from workspace memberships, not your email domain.`}>
        <Card className="divide-y divide-line">
          <div className="flex items-center justify-between gap-4 px-4 py-3"><span className="text-ink-2">Role in this workspace</span><span className="font-medium">{role}</span></div>
          <div className="flex items-center justify-between gap-4 px-4 py-3"><span className="text-ink-2">Session ends</span><span className="font-medium">{absolute(session.expiresAt)}</span></div>
          <div className="flex items-center justify-between gap-4 px-4 py-3"><span className="text-ink-2">Times are shown in</span><span className="font-medium">{timeZone()}</span></div>
          <div className="px-4 py-3"><Button onClick={() => void signOut()} disabled={signingOut}><LogOut className="size-4" />{signingOut ? 'Signing out…' : 'Sign out'}</Button></div>
        </Card>
      </Section>
      <MyInvitations />
      {session.managesPeopleIn.includes(mspId) && (
        <Section id="people" title="People" description="Who works in this workspace, which customers each person can reach, and what they can do there. Invitations go by email; only addresses on the allowlist can be invited.">
          <People />
        </Section>
      )}
      <Section id="second-factors" title="Second factors" description="Every sign-in asks for a second factor after Google: a touch of an approval key, a code from an authenticator app, or a recovery code.">
        <SecondFactors />
      </Section>
      <Section id="keys" title="Approval keys" description="Jobs that change a device are approved with a security key or passkey, separately from Google sign-in. Each approval signs the exact script, device and expiry. The same keys sign you in when Google isn’t available.">
        <ApprovalKeys />
      </Section>
      <Section title="Your permissions" description="Granted per membership. A role name alone grants nothing; viewing a device never implies running anything on it.">
        <div className="flex flex-wrap gap-2">
          {myCaps.length ? myCaps.map(c => <span key={c} className="rounded-full border border-line bg-panel px-3 py-1 text-[13px]">{capabilityLabel[c]}</span>)
            : <span className="text-[13px] text-ink-3">View only</span>}
        </div>
      </Section>
      <Section id="enrollment" title="Add devices" description="Download an installer for a customer site. Devices it installs on are placed at that site; it cannot place them elsewhere.">
        <Enrollment />
      </Section>
      <Section id="agent-access" title="Agent access" description="Give an AI agent or script its own credential to list customers and sites, make installers for the customer and site you choose, and add the customers you name. It works within your access, does only what you tick, and every action is in the audit trail as that agent.">
        <AgentAccess />
      </Section>
      <Section id="support-contact" title="Support contact for end users" description="Appears in the device tray’s Get help on Windows devices running agent 0.7.1 or later. A link, phone number or email, each optional. Each support action needs its contact: Email support needs an email, Call needs a phone, and Submit a ticket needs a link or email, or the service desk form when that is on.">
        <SupportContactSetting />
      </Section>
      <Section title="Workspaces" description="Each workspace is a separate MSP. Switching clears everything loaded from the previous one.">
        <SettingsList id="workspaces" title="Available workspaces" items={workspaces}>{visible => <Card className="divide-y divide-line">
          {visible.map(w => (
            <div key={w.id} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1"><p className="font-medium">{w.name}</p><p className="text-[13px] text-ink-3">{w.coverage}</p></div>
              {w.id === mspId && <span className="inline-flex items-center gap-1 text-[12.5px] font-medium text-brand-ink"><Check className="size-3.5" />Current</span>}
            </div>
          ))}
        </Card>}</SettingsList>
      </Section>
      <Section id="sources" title="Data sources" description="What search, Today and briefs can see. Anything not connected is never reported as healthy.">
        <SettingsList id="sources" title="Sources" items={sourcesFor(session.dataset, fleet.data?.agent, !!session.remoteAccess, !!session.serviceDesk)}>{visible => <Card className="divide-y divide-line">
          {visible.map(s => (
            <div key={s.key} className="flex items-start gap-3 px-4 py-3">
              {s.connected ? <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', s.demo ? 'bg-attention' : 'bg-healthy')} /> : <CircleSlash className="mt-0.5 size-4 shrink-0 text-ink-3" />}
              <div className="min-w-0 flex-1">
                <p className={cn('font-medium', !s.connected && 'text-ink-2')}>{s.name}</p>
                <p className="mt-0.5 text-[13px] text-ink-3">{s.detail}</p>
              </div>
              <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[12px] font-medium',
                s.demo ? 'bg-attention-soft text-attention' : s.connected ? 'bg-healthy-soft text-healthy' : 'bg-sunken text-ink-3')}>
                {s.demo ? 'Demo data' : s.connected ? 'Connected' : 'Not connected'}
              </span>
            </div>
          ))}
        </Card>}</SettingsList>
      </Section>
      <Section title="Appearance">
        <div role="radiogroup" aria-label="Theme" className="flex flex-wrap gap-2">
          {themes.map(([value, label, Icon]) => (
            <button key={value} type="button" role="radio" aria-checked={theme === value} onClick={() => setTheme(value)}
              className={cn('inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-[13.5px] font-medium transition-colors',
                theme === value ? 'border-brand/50 bg-brand-soft text-ink' : 'border-line bg-panel text-ink-2 hover:border-line-strong')}>
              <Icon className="size-4" />{label}
            </button>
          ))}
        </div>
      </Section>
      <Section title="Keyboard">
        <Button onClick={showShortcuts}><Keyboard className="size-4" />Show keyboard shortcuts</Button>
      </Section>
    </div>
    </SettingsListPreferences>
  );
}
